<script setup lang="ts">
import model from "@/ts/model.ts";
import NavLink from "./../nav-link.vue";
import { navigateTo } from "@/ts/management/view-navigation-management";
import { defineAsyncComponent } from "vue";
import definedCssVals from "@/ts/defined-css-vals.ts";
import { getLangText } from "@/ts/utils/model-utils";
import MobileNavBottom from "./mobile-nav-bottom.vue";
import MobileNavSide from "./mobile-nav-side/mobile-nav-side.vue";
import Btn from "../btn.vue";
import menuIcon from "@/assets/menu.svg?raw";
import MainNavUserMenuItemsWrapper from "./main-nav-user-menu-items-wrapper.vue";
import { globalBlurLogic, logoUrl, onLogoClick, shouldAppAdvertBeDisplayed, showMobileSideNav, smallLogoUrl } from "@/ts/management/ui-management";
import NavBtnInbox from "./nav-btn-inbox.vue";
import vPreloadImg from "@/ts/directives/v-preload-img.ts";
import LangSelector from "../lang-selector/lang-selector.vue";
import LoadingSpinner from "../loading-spinner.vue";
import { UserBalanceAmount } from "@/ts/management/user-management.ts";

const constTranslateY = (definedCssVals.MAIN_NAV_DESKTOP_TRANSFORM_Y * -1) + 'px';
const constTranslateYPositive = definedCssVals.MAIN_NAV_DESKTOP_TRANSFORM_Y + 'px';

const NavBtnStellarStream = defineAsyncComponent({
    loader: () => import("./nav-btn-stellar-stream.vue"),
    loadingComponent: LoadingSpinner,
});

function onLoginClick(){
    navigateTo('login');
}

const vNav = {
    unmounted(){
        delete model.elemRefs.mainNav;
    },
    mounted(element: HTMLElement){
        model.elemRefs.mainNav = element;
    },
}
</script>

<template>
    <div 
        id="nav" 
        :class="{
            'on-top': model.uiData.atTop, 
            'blur': globalBlurLogic,
            'gameplay-view-displayed': model.view == 'gameplay',
            'should-app-advert-be-displayed': shouldAppAdvertBeDisplayed(),
        }" 
        v-mobile 
        v-nav 
        v-has-app-header-advert
        v-is-app
    >
        <template v-if="model.isMobile">
            <div class="col" @click.prevent.stop="onLogoClick()">
                <img v-preload-img="smallLogoUrl" class="logo-small" width="50" height="50">
                <div class="menu-icon" v-html="menuIcon" @click.prevent.stop="showMobileSideNav(true)"></div>
            </div>
            <template v-if="model.view != 'gameplay'">
                <template v-if="model.userLoggedIn">
                    <div class="col">
                        <div class="button-and-credit-meter">
                            <btn :skin="'secondary'" @click.prevent="navigateTo('deposit-standalone')">{{ getLangText('DepositLabel', undefined, 'btn') }}</btn>
                            <div class="credit-meter" @click.prevent="navigateTo('user-stats')">{{ UserBalanceAmount }}</div>
                        </div>
                    </div>
                    <div class="col">
                        <nav-btn-inbox />
                    </div>
                    <div class="col">
                        <main-nav-user-menu-items-wrapper />
                    </div>
                </template>
                <template v-else>
                    <div class="col col-btns" v-mobile>
                        <template v-if="!model.credentialsStored">
                            <btn :skin="'secondary'" @click.prevent.stop="navigateTo('register')">{{ getLangText('RegisterLabel') }}</btn>
                        </template>
                        <btn :skin="'primary'" @click.prevent.stop="onLoginClick()">{{ getLangText('EnterLabel', undefined, 'btn') }}</btn>
                    </div>
                </template>
            </template>
        </template>
        <template v-else>
            <div class="upper-menu">
                <div class="logo">
                    <img :src="logoUrl" @click.prevent.stop="onLogoClick()">
                </div>
                <template v-if="model.userLoggedIn">
                    <div class="user-menu">
                        <div class="button-and-credit-meter">
                            <btn :skin="'secondary'" @click.prevent="navigateTo('deposit-standalone')">{{ getLangText('DepositLabel', undefined, 'btn') }}</btn>
                            <template v-if="model.view != 'gameplay'">
                                <div class="credit-meter" @click.prevent="navigateTo('user-stats')">{{ UserBalanceAmount }}</div>
                            </template>
                        </div>
                        <nav-btn-inbox />
                        <template v-if="model.bonusPointsType == 'stellar-stream' && model.userLoggedIn">
                            <nav-btn-stellar-stream :location="'desktop-menu'" :small-logo="true" :width-auto="true" />
                        </template>
                        <main-nav-user-menu-items-wrapper />
                        <lang-selector />
                    </div>
                </template>
                <template v-else>
                    <div class="btns">
                        <template v-if="!model.credentialsStored">
                            <btn :skin="'secondary'" @click.prevent.stop="navigateTo('register')">{{ getLangText('RegisterLabel') }}</btn>
                        </template>
                        <btn :skin="'primary'" @click.prevent.stop="onLoginClick()">{{ getLangText('EnterLabel', undefined, 'btn') }}</btn>
                        <lang-selector />
                    </div>
                </template>
            </div>
            <template v-if="model.view != 'gameplay'">
                <div class="nav-menu">
                    <template v-for="item in model.clientConfig.nav" :key="item.id">
                        <template v-if="item.onMenu">
                            <nav-link v-bind="item" class="menu-item" :class="{active: model.view == item.id}" location="nav" />
                        </template>
                    </template>
                </div>
            </template>
        </template>
    </div>
    <template v-if="model.isMobile">
        <teleport to="body" defer>
            <mobile-nav-side />
        </teleport>
        <mobile-nav-bottom />
    </template>
</template>

<style lang="scss" scoped>
@use '@/scss/shared';
@use 'sass:color';
#nav{
    position: fixed;
    top:0;
    left:0;
    display: flex;
    z-index: 1;
    flex-direction: column;
    width: 100%;
    justify-content: center;
    align-items: center;
    padding-top: 30px;
    z-index: 210;
    $transition-speed: .20s;
    transition: background-color $transition-speed, transform $transition-speed, opacity .25s;
    &[data-header-advert-displayed]{
        top:60px;
        &[data-mobile]:not(.on-top){
            transform: translateY(-60px);
        }
    }
    &.blur{
		opacity: .15;
    }

    &[data-mobile]{
        display: flex;
        flex-direction: row;
        width: 100%;
        padding-top: 10px;
        padding-bottom: 10px;
        padding-left: 0;
        padding-right: 0;
        justify-content: space-between;
        &.gameplay-view-displayed{
            width: auto !important;
        }
        & > .col{
            position:relative;
            display: flex;
            justify-content: center;
            align-items: center;
            align-self: stretch;
            &.col-btns[data-mobile]{
                padding-right: 5px;
            }
            & > .menu-icon{
                display: flex;
                justify-content: center;
                align-items: center;
                @include shared.site() using($site-key){
                    color: shared.theme-color("comp3_3", $site-key);
                }
            }
            &:nth-child(1){
                display: flex;
                justify-content: flex-start;
                flex: 1;
            }
            &:nth-child(2){
                display: flex;
                flex: 1;
                justify-content: flex-end;
                align-items: center;
            }
            & > .burger-btn{
                width: 45px;
            }
            & > .logo,
            & > .logo-small{
                pointer-events: none;
            }
            & > .logo{
                width: 150px;
            }
            & > .logo-small{
                width: 50px;
                padding: 5px;
                box-sizing: border-box;
            }
            & > .button-and-credit-meter{
                display: flex;
                justify-content: center;
                align-items: center;
                border-radius: 100px;
                & > .credit-meter{
                    font-size: 13px !important;
                    padding: 0 3px;
                    @include shared.casino-section-title();
                }
            }
        }
    }
    & > .upper-menu{
        @mixin btns-upper-menu(){
            margin: 0 5px;
            padding: 12px 12px;
            min-width: 170px;
            font-size: 17px;
        }
        position: relative;
        transition: transform $transition-speed;
        align-self: stretch;
        max-width: shared.$max-resolution;
        width: 100%;
        margin: 0 auto;
        z-index: 1;
        & > .user-menu{
            position: relative;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            padding-right: 20px;
            & > .button-and-credit-meter{
                display: flex;
                justify-content: center;
                align-items: center;
                margin-right: 10px;
                border-radius: 100px;
                & > .credit-meter {
                    padding: 0 5px;
                    padding-right: 20px;
                    font-weight: 700;
                    text-shadow: 2px 2px 0 #00000085;
                    cursor: pointer;
                    transition: color .25s;
                    font-size: 17px;
                    @include shared.casino-section-title();
                    &:hover{
                        @include shared.site() using($site-key){
                            color: shared.theme-color("comp3_3", $site-key);
                        }
                    }
                }
            }
        }
        & > .btns{
            position: relative;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            padding: 0 20px;
        }
        & > .logo{
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 50%;
            transition: transform .25s;
            transform: translateX(-50%) translateY(0) scale(1);
            z-index: 1;
            & > img{
                transition: transform .25s;
                &:hover{
                    transform: scale(1.1);
                }
                @include shared.site() using($site-key){
                    @if $site-key == "sky"{
                        height: 70px;
                    }
                }
            }
        }
    }
    & > .nav-menu{
        display: flex;
        padding: 20px 0;
        transition: transform $transition-speed;
        & > .menu-item{
            padding: 20px 35px;
            text-transform: uppercase;
            font-weight: 700;
            cursor: pointer;
            transition: color $transition-speed;
            &:link,
            &:active,
            &:visited{
                color: #fff;
                text-decoration: none;
            }
            &:hover,
            &.active{
                @include shared.site() using($site-key){
                    color: shared.theme-color("comp3_3", $site-key);
                }
            }
        }
    }
    &:not(.on-top){
        background-color: color.adjust(#000, $alpha: -.10);
        &:not([data-mobile]){
            // transform: translateY(v-bind(constTranslateY));
            // &:not([data-is-app]),
            // &:not(.should-app-advert-be-displayed){
            //     transform: translateY(calc(v-bind(constTranslateY) * 2));
            // }

            &[data-is-app]{
                transform: translateY(v-bind(constTranslateY));
            }
            &:not([data-is-app]){
                &.should-app-advert-be-displayed{
                    transform: translateY(calc(v-bind(constTranslateY) * 2));
                }
                &:not(.should-app-advert-be-displayed){
                    transform: translateY(v-bind(constTranslateY));
                }
            }
        }
        & > .upper-menu{
            transform: translateY(v-bind(constTranslateYPositive));
            & > .logo{
                transform: translateX(-50%) translateY(-30%) scale(.75);
            }
            & > img{
                transform: scale(.75) translateY(v-bind(constTranslateYPositive));
            }
        }
        & > .nav-menu{
            transform: scale(.75) translateY(25px);
        }
    }
}
</style>